import React from 'react';
import { useNavigate } from 'react-router';
import { Avatar } from '@heroui/react';
import { RefShareOrder, OwnerLevel } from '../../../../api/services';
import { useTranslations } from '../../../../contexts/translation.context';

interface OwnerCellProps {
  order: RefShareOrder;
}

const levelConfig: Record<OwnerLevel, { bgColor: string; textColor: string; label: string }> = {
  new: { bgColor: 'rgba(23, 201, 100, 0.15)', textColor: '#17C964', label: 'New' },
  base: { bgColor: '#EEEEEF', textColor: '#71717A', label: 'Base' },
  pro: { bgColor: '#11181c', textColor: '#ffffff', label: 'Pro' },
  expert: { bgColor: 'rgba(120, 40, 200, 0.15)', textColor: '#7828c8', label: 'Expert' },
  bronze: { bgColor: '#FDF4E7', textColor: '#CD7F32', label: 'Bronze' },
  silver: { bgColor: '#F0F0F0', textColor: '#808080', label: 'Silver' },
  gold: { bgColor: '#FFF9E6', textColor: '#FFD700', label: 'Gold' },
  platinum: { bgColor: '#E8F4F8', textColor: '#A0B2C6', label: 'Platinum' },
  diamond: { bgColor: '#E6F3FF', textColor: '#00BFFF', label: 'Diamond' },
};

const OwnerCell: React.FC<OwnerCellProps> = ({ order }) => {
  const navigate = useNavigate();
  const { t } = useTranslations();

  // Owner (creator/agency) comes from offer.client
  const client = order.offer?.client;
  if (!client) {
    return <span className="text-[#71717A]">—</span>;
  }

  const clientId = client._id || client.id || '';
  const clientType = client.type || 'creator';
  const href = `/users/creators/${clientId}`;
  const level = client.lvl;
  const levelStyle = level ? levelConfig[level] : null;

  return (
    <div className="flex items-center gap-[12px] py-2">
      <Avatar
        src={client.avatar}
        showFallback
        name={client.name?.charAt(0)}
        className="w-[40px] h-[40px] rounded-[12px] bg-gray-200 uppercase text-[14px] font-medium"
        classNames={{
          fallback: "bg-gray-200 text-gray-600"
        }}
      />
      <div className="flex flex-col gap-[4px]">
        <span
          onClick={() => navigate(href)}
          className="text-[#006FEE] text-[14px] font-semibold hover:underline cursor-pointer"
        >
          {client.name}
        </span>
        <div className="flex items-center gap-[6px]">
          {levelStyle && (
            <span
              className="inline-flex items-center justify-center rounded-lg px-[6px] py-[2px] text-[11px] font-medium"
              style={{
                backgroundColor: levelStyle.bgColor,
                color: levelStyle.textColor,
              }}
            >
              {levelStyle.label}
            </span>
          )}
          {/* Type Badge - Creator/Agency */}
          <span
            className="inline-flex items-center justify-center rounded-lg px-[6px] py-[2px] text-[11px] font-medium"
            style={{
              backgroundColor: clientType === 'agency' ? 'rgba(120, 40, 200, 0.2)' : 'rgba(0, 111, 238, 0.2)',
              color: clientType === 'agency' ? '#7828C8' : '#006FEE'
            }}
          >
            {clientType === 'agency' ? t('admin.creators.type.agency') : t('admin.creators.type.creator')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default OwnerCell;
